import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getOrganizationAnalyticsId, trackOrganizationEvent } from '@/analytics/utils/organization-analytics'
import { AuthContext } from '@/context/auth-context'
import signOutOfApp from '@/firebase/auth/sign-out'
import { postLeaveOrganization } from '@/organizations/fetch/post-leave-organization'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

import LeaveOrganizationModal from './LeaveOrganizationModal'

interface LeaveOrganizationModalContainerProps {
  visible: boolean
  organizationName: string
  onClose: () => void
  stacked?: boolean
}

const GENERIC_ERROR = 'Could not leave the organization. Please try again.'
const SIGN_OUT_ERROR = 'You left the organization, but we could not sign you out. Please try again.'

function isAlreadyRemovedError(error: ApiError): boolean {
  return error.status === 400 && typeof error.detail === 'string' && error.detail.includes('already removed')
}

/**
 * True when the server almost certainly removed the membership even though the client did not get a
 * clean success. Both cases must complete the intended end state (logout) instead of stranding the
 * detached user in an authenticated UI:
 * - ApiResponsePayloadError: the request returned a 2xx status, so the leave committed; only the
 *   response body failed to parse or validate.
 * - "already removed" 400: a retry after the membership was already removed on a prior attempt.
 *
 * A network error (never reaches the server) or a 5xx (the transaction rolled back) is a genuine
 * failure and is intentionally not treated as complete; the "already removed" retry path recovers
 * the rare case where a committed leave's response was lost in transit.
 */
function isLeaveEffectivelyComplete(error: unknown): boolean {
  if (error instanceof ApiResponsePayloadError) return true
  return error instanceof ApiError && isAlreadyRemovedError(error)
}

function mapLeaveOrganizationError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof TypeError ? 'Network error. Check your connection and try again.' : GENERIC_ERROR
  }

  if (error.status === 400 && typeof error.detail === 'string' && error.detail.includes('billing admin')) {
    return "The organization owner can't leave. Transfer ownership before trying again."
  }
  if (error.status === 409) {
    const detail = error.detail && typeof error.detail === 'object' ? (error.detail as Record<string, unknown>) : null
    if (detail?.['error'] === 'MATTER_NAME_CONFLICT') {
      return 'A matter with the same name already belongs to the organization owner. Rename it and try again.'
    }
    return 'Your matters could not be transferred. Please try again.'
  }
  if (error.status === 429) {
    return 'Please wait a moment before trying again.'
  }
  if (error.status === 404) {
    return 'Your account could not be found. Refresh the page and try again.'
  }

  return GENERIC_ERROR
}

/** Owns the leave request, request state, error mapping, and post-success logout. */
export default function LeaveOrganizationModalContainer({ visible, organizationName, onClose, stacked = false }: LeaveOrganizationModalContainerProps) {
  const navigate = useNavigate()
  const { userAccountData } = useContext(AuthContext)
  const { trackEvent } = useAnalytics()
  const [isLeaving, setIsLeaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    if (isLeaving) return

    setIsLeaving(true)
    setError(null)

    try {
      await postLeaveOrganization()
    } catch (caught) {
      // A committed-but-not-cleanly-acknowledged leave still logs the user out, so a detached user
      // is never stranded in an authenticated UI (see isLeaveEffectivelyComplete).
      if (!isLeaveEffectivelyComplete(caught)) {
        setError(mapLeaveOrganizationError(caught))
        setIsLeaving(false)
        return
      }
    }

    trackOrganizationEvent(trackEvent, AnalyticsEvent.OrganizationLeft, {
      organizationId: getOrganizationAnalyticsId(userAccountData),
    })

    try {
      await signOutOfApp(navigate)
    } catch {
      setError(SIGN_OUT_ERROR)
      setIsLeaving(false)
    }
  }

  return (
    <LeaveOrganizationModal
      visible={visible}
      organizationName={organizationName}
      onClose={onClose}
      onConfirm={handleConfirm}
      isLeaving={isLeaving}
      error={error}
      stacked={stacked}
    />
  )
}
